import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {pathToURL} from 'BenchlingUtil';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  AnalyticsDashboardActions,
  deleteAnalyticsDashboard,
} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import {INSIGHTS_HELP_URL} from 'insights/InsightsConstants';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import EditParametersModal from 'insights/parameters/EditParametersModal';
import {useWritableContext} from 'insights/WritableContext';
import {ButtonSize} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import runModal from 'ui-library/runModal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {withLoadingScreen} from 'util/Promises';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import useAreAnyAnalyticsQueriesRunning from '../data/useAreAnyAnalyticsQueriesRunning';
import AnalyticsDashboardAskInput from './AnalyticsDashboardAskInput';
import {useAnalyticsDashboardContext} from './AnalyticsDashboardDetailsContext';
import AnalyticsDashboardSettingsButton from './AnalyticsDashboardSettingsButton';
import CloneAnalyticsDashboardModal from './CloneAnalyticsDashboardModal';
import {GetAnalyticsDashboard} from './graphql/__generated/GetAnalyticsDashboard';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type ConnectedActions = Pick<AnalyticsDashboardActions, 'deleteAnalyticsDashboard'> & {
  removeFromWorkspace: typeof FileBrowserActions.removeFromWorkspace;
};

interface UnconnectedActions {
  updateParameterConfigsAndValues: (
    newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
    newParameterValues: Array<AnalyticsParameterValue>
  ) => void;
  updateParameterValues: (newParameterValues: Array<AnalyticsParameterValue>) => void;
}

export interface AnalyticsDashboardActionButtonsProps {
  actions: ConnectedActions & UnconnectedActions;
  analyticsDashboard: AnalyticsDashboard;
  className?: string;
}

export function AnalyticsDashboardActionButtons(
  props: AnalyticsDashboardActionButtonsProps
): JSX.Element | null {
  const writable = useWritableContext();
  const {analyticsDashboardViewStateId, showDashboardCloneButton} = useAnalyticsDashboardContext();

  function handleCopyLink(): void {
    copyToClipboard(pathToURL(props.analyticsDashboard.url), {showToastOnSuccess: true});
  }

  function handleCloneDashboard(): void {
    runModal(CloneAnalyticsDashboardModal, {analyticsDashboardId: props.analyticsDashboard.id});
  }

  function handleEditParameters(): void {
    runModal(EditParametersModal, {
      parameterConfigs: props.analyticsDashboard.parameterConfigs,
      parameterValues: props.analyticsDashboard.parameterValues,
      onRequestSubmit: ({
        parameterConfigs,
        parameterValues,
      }: {
        parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
        parameterValues: Array<AnalyticsParameterValue>;
      }): void => {
        if (writable) {
          props.actions.updateParameterConfigsAndValues(parameterConfigs, parameterValues);
        } else {
          props.actions.updateParameterValues(parameterValues);
        }
      },
      writable,
    });
  }

  async function handleDelete(): Promise<void> {
    if (await runConfirmDeleteModal({typeName: 'dashboard'})) {
      await withLoadingScreen(
        props.actions.deleteAnalyticsDashboard(props.analyticsDashboard.id),
        'Deleting dashboard...'
      );
      props.actions.removeFromWorkspace(props.analyticsDashboard.id);
    }
  }

  assertAndContinue(
    !writable || isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DELETE}),
    'Expected INSIGHTS_DELETE to be enabled if INSIGHTS_EDIT is enabled'
  );

  const areQueriesRunning = useAreAnyAnalyticsQueriesRunning(
    props.analyticsDashboard,
    analyticsDashboardViewStateId
  );
  const overflowMenuOptions: Array<OverflowMenuOption> = _.compact([
    {
      icon: 'link',
      contents: <span>Copy link to dashboard</span>,
      onSelect: handleCopyLink,
    },
    showDashboardCloneButton && {
      icon: 'copy',
      contents: <span>Duplicate</span>,
      onSelect: handleCloneDashboard,
    },
    writable && {
      icon: 'delete',
      contents: <span>Delete</span>,
      onSelect: handleDelete,
    },
    props.analyticsDashboard.parameterConfigs.length > 0 && {
      icon: 'edit',
      isDisabled: areQueriesRunning,
      tooltipTitle: areQueriesRunning
        ? "Parameter values can't be changed while queries are running"
        : undefined,
      contents: <span>Edit parameters</span>,
      onSelect: handleEditParameters,
      dataTestAttrs: {
        component: 'AnalyticsDashboardActionButtons',
        element: 'editParametersOption',
      },
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([props.analyticsDashboard.id]);
      },
    },
    {
      icon: 'open',
      contents: <span>Help with dashboards</span>,
      href: INSIGHTS_HELP_URL,
      openInNewTab: true,
    },
  ]);

  const actionButtons = _.compact([
    <AnalyticsDashboardAskInput dashboardId={props.analyticsDashboard.id} key="ask" />,
    writable && (
      <AnalyticsDashboardSettingsButton
        analyticsDashboard={props.analyticsDashboard}
        className="u-mini-spacing-left"
        key="settings"
      />
    ),
    <OverflowMenu
      buttonProps={{
        size: ButtonSize.SMALL,
        dataTestAttrs: {component: 'AnalyticsDashboardActionButtons', element: 'overflowMenu'},
      }}
      enableTooltip={true}
      key="overflow"
      menuProps={{dropLeft: true}}
      options={overflowMenuOptions}
      tooltipPlacement={FloatingPlacement.LEFT}
    />,
  ]);

  return <div className={props.className}>{actionButtons}</div>;
}

export default function ConnectedAnalyticsDashboardActionButtons(
  props: Omit<AnalyticsDashboardActionButtonsProps, 'actions'> & {actions: UnconnectedActions}
): JSX.Element {
  return (
    <AnalyticsDashboardActionButtons
      {...props}
      actions={{
        ...props.actions,
        deleteAnalyticsDashboard,
        removeFromWorkspace: FileBrowserActions.removeFromWorkspace,
      }}
    />
  );
}
